import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * Handles scroll behavior on client-side navigation.
 *
 * - Route change WITHOUT a hash: jump to the top of the destination page
 *   (instant) so the user never lands on a stale scroll position.
 * - Route change WITH a hash (#section): wait for the destination to mount,
 *   retrying briefly to account for lazy-loaded routes / hydration timing,
 *   then smooth-scroll to the target element.
 * - If the target section cannot be resolved in time: fall back to scrolling
 *   to the top of the destination page.
 *
 * Important: Lenis maintains its own internal scroll position via
 * momentum smoothing. A bare `window.scrollTo(0, 0)` will visually
 * jump but Lenis will then rubber-band back to its cached position.
 * We call `window.__lenis?.scrollTo(0, { immediate: true, force: true })`
 * (exposed by SmoothScroll) whenever it exists so the two stay in sync.
 *
 * Mounted once inside <BrowserRouter>. Renders nothing.
 */
export default function ScrollManager() {
  const { pathname, hash, key } = useLocation();

  useEffect(() => {
    const scrollTopInstant = () => {
      const lenis = typeof window !== "undefined" ? window.__lenis : null;
      if (lenis) {
        lenis.scrollTo(0, { immediate: true, force: true });
      }
      window.scrollTo(0, 0);
    };

    if (!hash) {
      // Reset immediately, then again after the lazy route commits, so a
      // late-mounting (Suspense) page cannot leave a stale scroll position.
      scrollTopInstant();
      const raf = window.requestAnimationFrame(scrollTopInstant);
      const t = window.setTimeout(scrollTopInstant, 120);
      return () => {
        window.cancelAnimationFrame(raf);
        window.clearTimeout(t);
      };
    }

    // Hash target: retry to handle lazy route mount / hydration timing.
    let attempts = 0;
    let timer;
    const target = () => {
      const raw = hash.startsWith("#") ? hash.slice(1) : hash;
      return (
        document.getElementById(raw) ||
        document.querySelector(`[data-testid="${raw}"]`) ||
        (() => {
          try {
            return document.querySelector(hash);
          } catch {
            return null;
          }
        })()
      );
    };

    const tryScroll = () => {
      const el = target();
      if (el) {
        const lenis = typeof window !== "undefined" ? window.__lenis : null;
        if (lenis) {
          lenis.scrollTo(el, { offset: 0 });
        } else {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
        return;
      }
      if (attempts < 12) {
        attempts += 1;
        timer = window.setTimeout(tryScroll, 100);
      } else {
        // Section never resolved: never leave the user on a stale position.
        scrollTopInstant();
      }
    };

    timer = window.setTimeout(tryScroll, 60);
    return () => window.clearTimeout(timer);
  }, [pathname, hash, key]);

  return null;
}
